import React, { useMemo, useState } from 'react';
import { BarChart3Icon, BookOpenIcon, BoxesIcon, ClipboardListIcon, FilePlus2Icon, FileTextIcon, HomeIcon, LockIcon, MenuIcon, PackageIcon, ReceiptIcon, RepeatIcon, SettingsIcon, ShoppingCartIcon, StethoscopeIcon, Undo2Icon, UsersIcon, WalletIcon, XIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { BillingProvider, useBilling } from '../../contexts/BillingContext';
import { BillingNavigator, BillingSection, EditorPrefill, SalesDocType } from '../../types/billing';
import { billingRoles, BMED_LOGO } from '../../data/billingDefaults';
import { BillingHome } from './BillingHome';
import { SalesDocumentList } from './SalesDocumentList';
import { SalesDocumentEditor } from './SalesDocumentEditor';
import { SalesDocumentView } from './SalesDocumentView';
import { PaymentsReceipts } from './PaymentsReceipts';
import { RecordPaymentModal } from './RecordPaymentModal';
import { ItemsServices } from './ItemsServices';
import { DirectSaleEditor } from './DirectSaleEditor';
import { DirectSalesList } from './DirectSalesList';
import { BillingReports } from './reports/BillingReports';
import { BillingSettingsPage } from './settings/BillingSettingsPage';
import { BillingFlowHome } from './BillingFlowHome';
import { BillingSidebar, SidebarLink, SidebarPanel } from './BillingSidebar';
import { ItemFormModal } from './ItemFormModal';
import { BillingInventory } from './BillingInventory';

/** Shown for workspace sections whose screens are still being built in this release. */
function SectionInProgress({
  label
}: {
  label: string;
}) {
  return <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center" data-id="element-2953" data-mp="1yzf7lfnu4088">
      <h1 className="text-lg font-bold text-slate-900" data-id="element-2954" data-mp="1uyvbmfezz2no">{label}</h1>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-600" data-id="element-2955" data-mp="2rhj63128k2oy">This screen is not available yet. Invoices, quotations, orders, payments and receipts already work.</p>
    </div>;
}
type Overlay = {
  kind: 'editor';
  type: SalesDocType;
  id?: string;
  prefill?: EditorPrefill;
  key: number;
} | {
  kind: 'document';
  id: string;
} | {
  kind: 'account';
  id: string;
} | {
  kind: 'sale';
  key: number;
} | null;
type NavItem = {
  id: BillingSection;
  label: string;
  icon: typeof HomeIcon;
  count?: number;
};

/** Billing workspace — Reception and Administrator only (also enforced on every write). */
export function BillingModule() {
  const {
    currentUser
  } = useData();
  if (!currentUser || !billingRoles.includes(currentUser.role)) {
    return <div className="mx-auto mt-16 max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-id="element-2956" data-mp="72x5p9x6ik1y">
        <LockIcon className="mx-auto h-10 w-10 text-slate-400" aria-hidden="true" data-id="element-2957" data-mp="3oc3r7p2umnlg" />
        <h1 className="mt-3 text-lg font-bold text-slate-900" data-id="element-2958" data-mp="3tawu6nkiew7d">Access denied</h1>
        <p className="mt-1 text-sm text-slate-600" data-id="element-2959" data-mp="1miw25nxformd">Billing is available to Reception and Administrators.</p>
      </div>;
  }
  return <BillingProvider data-id="element-2960" data-mp="3bzhtznxhmm4p">
      <BillingShell data-id="element-2961" data-mp="3byynd3dafk5z" />
    </BillingProvider>;
}
function BillingShell() {
  const {
    isReady,
    documents,
    pendingCharges,
    can,
    config
  } = useBilling();
  const [section, setSection] = useState<BillingSection>('home');
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [payment, setPayment] = useState<{
    accountId?: string;
    invoiceId?: string;
    deposit?: boolean;
  } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [openSaleId, setOpenSaleId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const counts = useMemo(() => {
    const drafts = documents.filter(d => d.type === 'invoice' && d.state === 'Draft').length;
    const quotes = documents.filter(d => d.type === 'quotation' && (d.state === 'Draft' || d.state === 'Sent')).length;
    const orders = documents.filter(d => d.type === 'order' && d.state === 'Open').length;
    return {
      drafts,
      quotes,
      orders
    };
  }, [documents]);
  const [addingItem, setAddingItem] = useState(false);
  const [itemMessage, setItemMessage] = useState('');
  const link = (label: string, id: BillingSection, extra: Partial<SidebarLink> = {}): SidebarLink => ({
    label,
    onClick: () => nav.go(id),
    active: section === id && !overlay,
    ...extra
  });
  const panels: SidebarPanel[] = [{
    id: 'todo',
    label: 'To-Do',
    sections: ['home', 'summary', 'charges'],
    links: [link('Workflow home', 'home'), link('Billing summary & overdue', 'summary'), link('Unbilled hospital charges', 'charges', {
      count: pendingCharges.length
    })]
  }, {
    id: 'invoices',
    label: 'Invoices',
    sections: ['invoices', 'recurring'],
    links: [{
      label: 'New Invoice',
      onClick: () => nav.newDocument('invoice'),
      disabled: !can('createDocuments')
    }, link('Open Invoice List', 'invoices', {
      count: counts.drafts
    }), link('Recurring Invoices', 'recurring')]
  }, {
    id: 'quotes',
    label: 'Quotes/Orders',
    sections: ['quotations', 'orders'],
    links: [{
      label: 'New Quote',
      onClick: () => nav.newDocument('quotation'),
      disabled: !can('createDocuments')
    }, link('Open Quotes List', 'quotations', {
      count: counts.quotes
    }), {
      label: 'New Order',
      onClick: () => nav.newDocument('order'),
      disabled: !can('createDocuments')
    }, link('Open Orders List', 'orders', {
      count: counts.orders
    })]
  }, {
    id: 'payments',
    label: 'Payments',
    sections: ['payments', 'sales', 'credit-notes'],
    links: [{
      label: 'Apply Payment',
      onClick: () => nav.recordPayment(),
      disabled: !can('recordPayments')
    }, link('Open Payments & Receipts', 'payments'), {
      label: 'New Cash Sale',
      onClick: () => nav.newDirectSale(),
      disabled: !can('directSales') || !config.sales.directSalesEnabled
    }, link('Open Cash Sales List', 'sales'), link('Credit Notes List', 'credit-notes')]
  }, {
    id: 'customers',
    label: 'Customers',
    sections: ['accounts', 'statements'],
    links: [link('Open Customers List', 'accounts'), link('Customer Statements', 'statements')]
  }, {
    id: 'items',
    label: 'Items',
    sections: ['items', 'inventory'],
    links: [{
      label: 'Add New Item',
      onClick: () => can('manageCatalogue') ? setAddingItem(true) : setItemMessage('Your role cannot add items. An administrator can allow it in Billing Settings → Users & permissions ("Add and edit items").')
    }, link('Open Item List', 'items'), link('Open Inventory', 'inventory')]
  }, ...(can('viewReports') ? [{
    id: 'reports',
    label: 'Reports',
    sections: ['reports'] as BillingSection[],
    links: [link('Sales & Tax Reports', 'reports')]
  }] : []), {
    id: 'settings',
    label: 'Settings',
    sections: ['settings'],
    links: [link('Billing Settings', 'settings')]
  }];
  const groups: {
    label: string;
    items: NavItem[];
  }[] = [{
    label: 'Workspace',
    items: [{
      id: 'home',
      label: 'Billing Home',
      icon: HomeIcon
    }, {
      id: 'summary',
      label: 'Billing Summary',
      icon: HomeIcon
    }, {
      id: 'charges',
      label: 'Hospital Charges',
      icon: StethoscopeIcon,
      count: pendingCharges.length
    }]
  }, {
    label: 'Sales',
    items: [{
      id: 'invoices',
      label: 'Invoices',
      icon: FileTextIcon,
      count: counts.drafts
    }, {
      id: 'quotations',
      label: 'Quotations',
      icon: ClipboardListIcon,
      count: counts.quotes
    }, {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingCartIcon,
      count: counts.orders
    }, {
      id: 'recurring',
      label: 'Recurring Invoices',
      icon: RepeatIcon
    }]
  }, {
    label: 'Money',
    items: [{
      id: 'sales',
      label: 'Cash Sales / Direct Receipts',
      icon: ReceiptIcon
    }, {
      id: 'payments',
      label: 'Payments & Receipts',
      icon: WalletIcon
    }, {
      id: 'credit-notes',
      label: 'Credit Notes',
      icon: Undo2Icon
    }]
  }, {
    label: 'Accounts',
    items: [{
      id: 'accounts',
      label: 'Patient / Customer Accounts',
      icon: UsersIcon
    }, {
      id: 'statements',
      label: 'Statements',
      icon: BookOpenIcon
    }]
  }, {
    label: 'Setup',
    items: [{
      id: 'items',
      label: 'Items & Services',
      icon: PackageIcon
    }, {
      id: 'inventory',
      label: 'Inventory',
      icon: PackageIcon
    }, ...(can('viewReports') ? [{
      id: 'reports' as const,
      label: 'Reports',
      icon: BarChart3Icon
    }] : []), {
      id: 'settings',
      label: 'Billing Settings',
      icon: SettingsIcon
    }]
  }];
  const allItems = groups.flatMap(g => g.items);
  const nav: BillingNavigator = {
    go: next => {
      setSection(next);
      setOverlay(null);
      setMobileOpen(false);
    },
    newDocument: (type, prefill) => setOverlay({
      kind: 'editor',
      type,
      prefill,
      key: Date.now()
    }),
    editDocument: id => {
      const d = documents.find(x => x.id === id);
      if (d) setOverlay({
        kind: 'editor',
        type: d.type,
        id,
        key: Date.now()
      });
    },
    openDocument: id => setOverlay({
      kind: 'document',
      id
    }),
    openAccount: id => setOverlay({
      kind: 'account',
      id
    }),
    recordPayment: opts => setPayment(opts ?? {}),
    newDirectSale: () => setOverlay({
      kind: 'sale',
      key: Date.now()
    }),
    openDirectSale: id => {
      setSection('sales');
      setOverlay(null);
      setOpenSaleId(id);
    }
  };
  const toolbar = [{
    label: 'Invoice',
    icon: FilePlus2Icon,
    color: 'text-emerald-600',
    onClick: () => nav.newDocument('invoice'),
    disabled: !can('createDocuments')
  }, {
    label: 'Quote',
    icon: ClipboardListIcon,
    color: 'text-indigo-600',
    onClick: () => nav.newDocument('quotation'),
    disabled: !can('createDocuments')
  }, {
    label: 'Cash Sale',
    icon: ShoppingCartIcon,
    color: 'text-emerald-700',
    onClick: () => nav.newDirectSale(),
    disabled: !can('directSales') || !config.sales.directSalesEnabled
  }, {
    label: 'Payment',
    icon: WalletIcon,
    color: 'text-amber-600',
    onClick: () => nav.recordPayment(),
    disabled: !can('recordPayments')
  }, {
    label: 'Reports',
    icon: BarChart3Icon,
    color: 'text-blue-600',
    onClick: () => nav.go('reports'),
    disabled: !can('viewReports')
  }, {
    label: 'Home',
    icon: HomeIcon,
    color: 'text-slate-600',
    onClick: () => nav.go('home')
  }, {
    label: 'Items',
    icon: PackageIcon,
    color: 'text-sky-700',
    onClick: () => nav.go('items')
  }, {
    label: 'Inventory',
    icon: BoxesIcon,
    color: 'text-amber-700',
    onClick: () => nav.go('inventory')
  }, {
    label: 'Customers',
    icon: UsersIcon,
    color: 'text-teal-700',
    onClick: () => nav.go('accounts')
  }, {
    label: 'Options',
    icon: SettingsIcon,
    color: 'text-slate-600',
    onClick: () => nav.go('settings')
  }];
  const renderSection = () => {
    switch (section) {
      case 'home':
        return <BillingFlowHome nav={nav} data-id="element-2962" data-mp="1hfcazta3mg8q" />;
      case 'summary':
        return <BillingHome nav={nav} data-id="element-2963" data-mp="1v7aardhx6zs8" />;
      case 'invoices':
        return <SalesDocumentList type="invoice" nav={nav} data-id="element-2964" data-mp="1v9wj3fcmxjbi" />;
      case 'quotations':
        return <SalesDocumentList type="quotation" nav={nav} data-id="element-2965" data-mp="1r5x8tehy7gql" />;
      case 'orders':
        return <SalesDocumentList type="order" nav={nav} data-id="element-2966" data-mp="2556ma6errwqv" />;
      case 'payments':
        return <PaymentsReceipts nav={nav} data-id="element-2967" data-mp="206mye0gm8avv" />;
      case 'sales':
        return <DirectSalesList nav={nav} openId={openSaleId} onOpened={() => setOpenSaleId(null)} data-id="element-2968" data-mp="207y445tmfspm" />;
      case 'items':
        return <ItemsServices data-id="element-2969" data-mp="au1lrayzc0fr" />;
      case 'inventory':
        return <BillingInventory data-id="element-2970" data-mp="5u5i5cu72b0q" />;
      case 'reports':
        return can('viewReports') ? <BillingReports nav={nav} data-id="element-2971" data-mp="2hgqv71e3tf7y" /> : null;
      case 'settings':
        return <BillingSettingsPage data-id="element-2972" data-mp="2hh8wicfmijmq" />;
      default:
        return <SectionInProgress label={allItems.find(i => i.id === section)?.label ?? 'Billing'} data-id="element-2973" data-mp="2mg3ejc5ro45s" />;
    }
  };
  const content = () => {
    if (!isReady) {
      return <div role="status" className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600" data-id="element-2974" data-mp="27otghknw054h">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" aria-hidden="true" data-id="element-2975" data-mp="1yppxshsmtrqi" />
          Loading billing records…
        </div>;
    }
    if (overlay?.kind === 'editor') return <SalesDocumentEditor key={overlay.key} type={overlay.type} id={overlay.id} prefill={overlay.prefill} nav={nav} onClose={() => setOverlay(null)} data-id="element-2976" data-mp="1otctkpzgnvhi" />;
    if (overlay?.kind === 'document') return <SalesDocumentView id={overlay.id} nav={nav} onClose={() => setOverlay(null)} data-id="element-2977" data-mp="3j74cyh7ldtq5" />;
    if (overlay?.kind === 'account') return <SectionInProgress label="Account details" data-id="element-2978" data-mp="3o44golgyyke3" />;
    if (overlay?.kind === 'sale') return <DirectSaleEditor key={overlay.key} nav={nav} onClose={() => setOverlay(null)} data-id="element-2979" data-mp="3mpbithf5eu7f" />;
    return renderSection();
  };
  const navigation = (mobile = false) => <nav className="space-y-4" aria-label="Billing navigation" data-id="element-2980" data-mp="3cp16s04unstu">
      {groups.map(group => <div key={group.label} data-id="element-2981" data-mp="38oh7or0puf52">
          <p className={`chrome-label mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.16em] ${collapsed && !mobile ? 'sr-only' : ''}`} data-id="element-2982" data-mp="38pagryn5dgqd">{group.label}</p>
          <div className="space-y-0.5" data-id="element-2983" data-mp="4a0vuixqr0tc">
            {group.items.map(item => {
          const Icon = item.icon;
          const active = section === item.id && !overlay;
          return <button key={item.id} type="button" onClick={() => nav.go(item.id)} title={collapsed && !mobile ? item.label : undefined} aria-current={active ? 'page' : undefined} className={`flex w-full items-center rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors ${active ? 'chrome-nav-item-active' : 'chrome-nav-item'} ${collapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-2984" data-mp="3rnd3u7w4x3lj">
                  <Icon className={`h-4 w-4 shrink-0 ${collapsed && !mobile ? '' : 'mr-2.5'}`} aria-hidden="true" data-id="element-2985" data-mp="2avhmnqzhh1de" />
                  {(!collapsed || mobile) && <span className="truncate" data-id="element-2986" data-mp="2ey5xu5bo6kdj">{item.label}</span>}
                  {(!collapsed || mobile) && !!item.count && <span className="ml-auto rounded-full bg-white/20 px-1.5 text-[10px] font-bold tabular-nums" data-id="element-2987" data-mp="2jsrzvhvv30ky">{item.count}</span>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  const brand = <div className="flex items-center gap-3" data-id="element-2988" data-mp="2yl3neivi7oni">
      <img src={config.business.logoUrl || BMED_LOGO} alt="BMED Medical Centre" className="h-9 w-9 rounded-lg bg-white object-contain" data-id="element-2989" data-mp="2ymeq0fd7qqd1" />
      {(!collapsed || mobileOpen) && <div className="min-w-0" data-id="element-2990" data-mp="37mjtwfj776mg">
          <p className="truncate text-sm font-bold" data-id="element-2991" data-mp="3cjk2oy7fmnh9">BMED MEDICAL CENTRE</p>
          <p className="chrome-label truncate text-xs font-medium" data-id="element-2992" data-mp="3chphrjw5uvd6">Billing &amp; Invoicing</p>
        </div>}
    </div>;
  const title = overlay?.kind === 'editor' ? overlay.id ? 'Edit document' : `New ${overlay.type}` : overlay?.kind === 'document' ? 'Document' : overlay?.kind === 'account' ? 'Account' : overlay?.kind === 'sale' ? 'New cash sale' : allItems.find(i => i.id === section)?.label;
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-2993" data-mp="2fy8sbz0uosnu">
      <aside className="hidden w-[228px] shrink-0 flex-col border-r border-[#7d8185] bg-[#a3a6a9] lg:flex" data-id="element-2994" data-mp="2g01xf9vtf4tq">
        <div className="border-b border-[#8d9094] bg-[#d9dcdf] px-3 py-2.5 text-slate-900" data-id="element-2995" data-mp="2pwesoc70dqql">{brand}</div>
        <div className="flex-1 overflow-y-auto p-2.5" data-id="element-2996" data-mp="2kxbo0suxxmj5">
          <BillingSidebar key={section} panels={panels} current={section} data-id="element-2997" data-mp="2kvqrwysz9vj6" />
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-2998" data-mp="580is2bhliql">
          <button type="button" aria-label="Close billing navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setMobileOpen(false)} data-id="element-2999" data-mp="6b3ubl58kx8" />
          <aside className="relative flex h-full w-[260px] flex-col bg-[#a3a6a9] shadow-2xl" data-id="element-3000" data-mp="cpf4wal91d8e">
            <div className="flex items-center justify-between border-b border-[#8d9094] bg-[#d9dcdf] p-3 text-slate-900" data-id="element-3001" data-mp="3uvbeepmpc2ns">
              {brand}
              <button type="button" onClick={() => setMobileOpen(false)} className="rounded-md p-2 hover:bg-white/60" aria-label="Close billing navigation" data-id="element-3002" data-mp="2sags57uf7oy"><XIcon className="h-5 w-5" data-id="element-3003" data-mp="qjfwmkds9zz9" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-2.5" onClickCapture={e => {
          if ((e.target as HTMLElement).closest('li')) setMobileOpen(false);
        }} data-id="element-3004" data-mp="vfoishqeetfx">
              <BillingSidebar panels={panels} current={section} data-id="element-3005" data-mp="lpv4va2sa28i" />
            </div>
          </aside>
        </div>}

      <section className="min-w-0 flex-1" data-id="element-3006" data-mp="19ewidbqh43kn">
        <header className="sticky top-0 z-20 border-b-2 border-slate-800 bg-white" data-id="element-3007" data-mp="19duxkfjno2pq">
          <div className="flex items-stretch overflow-x-auto px-1 py-1" role="toolbar" aria-label="Billing actions" data-id="element-3008" data-mp="1e7z44xp0e8qa">
            <button type="button" onClick={() => setMobileOpen(true)} className="m-1 rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open billing navigation" data-id="element-3009" data-mp="1o0brzbo10evh"><MenuIcon className="h-5 w-5" data-id="element-3010" data-mp="1f42qlrghn66p" /></button>
            {toolbar.map((t, i) => {
            const Icon = t.icon;
            return <React.Fragment key={t.label} data-id="element-3011" data-mp="15427a5imcq2i" data-mpi={i}>
                  {(i === 5 || i === 9) && <span className="mx-1 my-1 w-px shrink-0 bg-slate-300" aria-hidden="true" data-id="element-3012" data-mp="10dc0cnujof5p" />}
                  <button type="button" onClick={t.onClick} disabled={t.disabled} className="flex min-w-[68px] shrink-0 flex-col items-center justify-center gap-1 rounded px-2 py-1 text-[12px] text-slate-900 transition-colors duration-150 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-3013" data-mp="wbi4csvanzbv">
                    <Icon className={`h-6 w-6 ${t.color}`} aria-hidden="true" data-id="element-3014" data-mp="7aats3j35uos" />
                    <span className="whitespace-nowrap" data-id="element-3015" data-mp="7cdo2zr9u9zb">{t.label}</span>
                  </button>
                </React.Fragment>;
          })}
          </div>
          <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-1.5 text-xs text-slate-500" data-id="element-3016" data-mp="h905feke2iwl">
            <span data-id="element-3017" data-mp="m57dpe09c6hp">Billing</span><span className="text-slate-300" data-id="element-3018" data-mp="3kpfkf8ja3deg">/</span><span className="font-semibold text-slate-800" data-id="element-3019" data-mp="3kqi9rc0yd7s9">{title}</span>
            {overlay && <button type="button" onClick={() => setOverlay(null)} className="ml-auto font-medium text-indigo-700 hover:underline" data-id="element-3020" data-mp="3ppbqddbjdewn">← Back to {allItems.find(i => i.id === section)?.label}</button>}
          </div>
        </header>
        <main className={section === 'home' && !overlay ? 'p-3 sm:p-5' : 'mx-auto max-w-[1600px] p-3 sm:p-5'} data-id="element-3021" data-mp="3a0fm3l53xyu">{content()}</main>
      </section>

      {addingItem && <ItemFormModal onClose={() => setAddingItem(false)} onSaved={m => {
      setAddingItem(false);
      setItemMessage(m ?? 'Item saved.');
      nav.go('inventory');
    }} data-id="element-3022" data-mp="36wr083n81nwj" />}
      {itemMessage && <div role="status" className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-md bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg" data-id="element-3023" data-mp="ky1fg3i0w3h0">
          {itemMessage}
          <button type="button" onClick={() => setItemMessage('')} className="text-slate-300 hover:text-white" aria-label="Dismiss" data-id="element-3024" data-mp="ty7qzju5utdj"><XIcon className="h-4 w-4" data-id="element-3025" data-mp="17wnteykzpxpv" /></button>
        </div>}
      {payment && isReady && <RecordPaymentModal {...payment} onClose={() => setPayment(null)} onSaved={id => {
      setPayment(null);
      setSection('payments');
      setOverlay(null);
      void id;
    }} data-id="element-3026" data-mp="13wx8hbi5abt9" />}
    </div>;
}